import { useParams } from "react-router-dom";
import { callQuery, LIDIA_GET_CONVERSATION, LIDIA_GET_LATEST_CONVERSATION, LIDIA_GET_PARTICIPANT_DETAILS, LLMVisitFeedback, PatientFileVersion, ACTIONABLE_ITEMS_FOR_VISIT, LidiaParticipantDetails } from "../../../../amplify/appsync/query";
import { PatientVisit } from "../../../../amplify/appsync/search-patients";
import { useEffect, useState, useRef } from "react";
import { ConversationStatus } from "../../../../amplify/appsync/query";
import useSetPagePatientVisit, { useGlobalOptions } from "../../../common/GlobalOptionsContext";
import { Col, Container, Row, Card, Button, Modal, Form, Toast, ToastContainer, Badge, ListGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle, faGear, faRefresh, faRightToBracket, faWandMagicSparkles, faTimes, faTrash, faUserPlus } from "@fortawesome/free-solid-svg-icons";
import { callWrite, LIDIA, LIDIA_CREATE_CONVERSATION, LIDIA_INITIATE_ACTIVITY, LIDIA_CLOSE_CONVERSATION, LIDIA_DELETE_CONVERSATION, LIDIA_ENROLL_PARTICIPANT } from "../../../../amplify/appsync/write";
import { dashboardLLMCache } from "../cache";
import { getLocalDateStrFromDate, getLocalTimeStrFromDate, parseAsUTC } from "../../../../utils/time";
import PersonalIdentifier from "../../../common/PersonalIdentifier";
import { getUserRole, UserRole } from "../../../../utils/user";
import { ActionableItem, Priority, Status } from "../common/models";

import './LidiaDashboard.scss';

// Define a simple type for a text message
type SMSMessage = {
    role: 'LIDIA' | 'PATIENT';
    content: string;
    timestamp: string;
    metadata?: any;
    message_type?: string;
    expect_patient_reply?: string;
};

// Mock data for the SMS conversation
const mockConversation: SMSMessage[] = [
    {
        "role": "LIDIA",
        "content": "Which finger is injured? Are you able to move the finger?",
        "metadata": {
            "questions": [
                "Which finger is injured?",
                "Are you able to move the finger?"
            ],
            "rationale": "Identifying which finger and checking function helps prioritize care and assess severity.",
            "priority_symptoms": [
                "specific finger",
                "range of motion"
            ],
            "message": "Which finger is injured? Are you able to move the finger?"
        },
        "timestamp": "2025-10-13T06:51:49.849516"
    },
    {
        "role": "PATIENT",
        "content": "No",
        "timestamp": "2025-10-13T07:03:41.109030"
    },
];

// Component to render the conversation timeline with custom styles
const ConversationTimeline = ({ messages }: { messages: SMSMessage[] }) => {
    
    // Custom styles for a modern chat look
    const clinicianColor = '#3b82f6'; // A professional blue (like Bootstrap primary)
    const patientColor = '#e5e7eb'; // A light gray for the patient's messages

    const getWaitTime = (message?: SMSMessage) => {
        if (message && message.metadata && message.metadata.wait_time !== undefined && message.metadata.wait_time !== null) {
            if (message.metadata.wait_time > 0) {
                return `Sent after ${message.metadata.wait_time} simulated days (${message.metadata.response_type})`;
            } else {
                return `Sent immediately (${message.metadata.response_type})`;
            }
        } else {
            return "";
        }
    };

    const getMessageType = (type?: string) => {
        if (type === "NEXT_STEP") {
            return "Next Step";
        } else if (type === "WR") {
            return "Waiting Room";
        } else if (type === "ROOM") {
            return "Room";
        } else if (type === "PDM") {
            return "Post-Discharge";
        } else if (type === "ACTIONABLE_ITEM") {
            return "Actionable Item";
        } else {
            return type;
        }
    };

    return (
        <div>
            <div className="d-flex flex-column gap-3 p-2">
                {messages.map((message) => {
                    const isClinician = message.role === 'LIDIA';
                    const time = getLocalDateStrFromDate(parseAsUTC(message.timestamp)) + " " + getLocalTimeStrFromDate(parseAsUTC(message.timestamp))

                    return (
                        <div
                            key={message.timestamp}
                            className={`d-flex ${isClinician ? 'justify-content-start' : 'justify-content-end'}`}
                        >
                            <div
                                className="p-3 rounded-4 shadow-sm"
                                style={{
                                    maxWidth: '80%',
                                    backgroundColor: isClinician ? clinicianColor : patientColor,
                                    color: isClinician ? 'white' : '#1f2937',
                                    borderTopLeftRadius: isClinician ? '0.5rem' : '1.5rem',
                                    borderTopRightRadius: isClinician ? '1.5rem' : '0.5rem',
                                    borderBottomLeftRadius: '1.5rem',
                                    borderBottomRightRadius: '1.5rem',
                                }}
                            >
                                <small className="fw-bold mb-1 d-block" style={{ fontSize: '0.75rem', opacity: 0.8 }}>
                                    {message.role === "LIDIA" ? "LIDIA" : "Patient"}
                                </small>
                                <p className="mb-1" style={{ fontSize: '0.95rem' }}>
                                    {message.content}
                                </p>
                                <div 
                                    className={`text-end mt-1 ${isClinician ? 'text-white-50' : 'text-muted'}`}
                                    style={{ fontSize: '0.7rem' }}
                                >
                                    <small>{getWaitTime(message) ? <>{getWaitTime(message)} &bull; </> : <></>}{getMessageType(message.message_type)} &bull; {message?.metadata?.expect_patient_reply === "yes" ? "Expecting Response" : "Not Expecting Response"} &bull; <PersonalIdentifier identifier={time} isTime /></small>
                                    {
                                        message.message_type === "NEXT_STEP" && message.metadata?.next_step?.action && (
                                            <div>
                                                <small>
                                                    Context: {message.metadata?.next_step?.action}
                                                </small>
                                            </div>
                                        )
                                    }
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// --------------------------------------------------------------------------------

export const LidiaDashboard = (props: {
    user: any,
    ws: string,
    tenant: string,
    patientVisit?: PatientVisit,
    showHeader?: boolean,
}) => {
    const { getDerivedRole } = useGlobalOptions();
    let { internalCSN } = useParams();
    const patientVisit = props.patientVisit;
    useSetPagePatientVisit(patientVisit);

    const [conversationStatus, setConversationStatus] = useState<ConversationStatus>();
    const [conversation, setConversation] = useState<SMSMessage[]>();
    const [isSavingOptIn, setIsSavingOptIn] = useState<boolean>(false);
    const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
    const [participantDetails, setParticipantDetails] = useState<LidiaParticipantDetails | null>(null);
    const [isLoadingParticipant, setIsLoadingParticipant] = useState<boolean>(false);

    const [showEnrollModal, setShowEnrollModal] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState("");
    const [participantType, setParticipantType] = useState("SIMULATED");
    const [isValidPhone, setIsValidPhone] = useState(false);
    const [isEnrolling, setIsEnrolling] = useState(false);

    // Actionable Items state
    const [showActionableModal, setShowActionableModal] = useState(false);
    const [actionableItems, setActionableItems] = useState<ActionableItem[]>([]);
    const [isLoadingActionableItems, setIsLoadingActionableItems] = useState(false);
    const [selectedActionableItem, setSelectedActionableItem] = useState<ActionableItem | null>(null);

    // Toast state
    const [showToast, setShowToast] = useState(false);

    // Ref to store the interval ID for cleanup
    const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // Function to check if patient is enrolled in LIDIA study
    const checkEnrollmentStatus = () => {
        if (!patientVisit) return;
        setIsLoadingParticipant(true);

        // Check if participant exists by looking for their study_id
        // We'll use a query to check by MRN and CSN
        callQuery({
            operation: LIDIA_GET_PARTICIPANT_DETAILS,
            mrn: patientVisit.mrn,
            csn: patientVisit.csn,
        })
        .then((details: LidiaParticipantDetails | null) => {
            // Handle null response (participant not found)
            if (details === null || details === undefined) {
                setParticipantDetails(null);
            } else {
                setParticipantDetails(details);
            }
        })
        .catch((error) => {
            console.error("Error checking enrollment status:", error);
            setParticipantDetails(null);
        })
        .finally(() => {
            setIsLoadingParticipant(false);
        });
    };

    // Function to fetch conversation data
    const fetchConversationData = () => {
        if (!patientVisit) return;
        setIsLoadingData(true);

        callQuery({
            operation: LIDIA_GET_CONVERSATION,
            csn: patientVisit.csn,
        })
        .then((conversation: any) => {
            setConversationStatus(conversation);
        })
        .catch((error) => {
            console.error("Error fetching conversation status:", error);
        })
        .finally(() => {
            setIsLoadingData(false);
        });

        dashboardLLMCache.get(LIDIA, patientVisit, props.tenant)
        .then((data: any) => {
            if (data && data.conversation_history) {
                setConversation(data.conversation_history);
            }
        })
        .catch((error) => {
            console.error("Error fetching conversation history:", error);
        });
    };

    // Function to fetch actionable items
    const fetchActionableItems = () => {
        if (!patientVisit) return;
        setIsLoadingActionableItems(true);

        callQuery({
            operation: ACTIONABLE_ITEMS_FOR_VISIT,
            mrn: patientVisit.mrn,
            csn: patientVisit.csn,
        })
        .then((items: ActionableItem[]) => {
            // Filter to only those assigned to LIDIA
            setActionableItems([
                ...items.filter((item) => item.executor_role === "LIDIA")
            ]);
        })
        .catch((error) => {
            console.error("Error fetching actionable items:", error);
        })
        .finally(() => {
            setIsLoadingActionableItems(false);
        });
    };

    // Initial fetch and setup polling
    useEffect(() => {
        if (patientVisit) {
            // Check enrollment status first
            checkEnrollmentStatus();

            // Fetch conversation data immediately
            fetchConversationData();

            // Set up polling every 30 seconds (adjust as needed)
            refreshIntervalRef.current = setInterval(() => {
                fetchConversationData();
            }, 30000); // 30 seconds

            // Cleanup function
            return () => {
                if (refreshIntervalRef.current) {
                    clearInterval(refreshIntervalRef.current);
                }
            };
        }
    }, [patientVisit, props.tenant]);

    // Validate E.164 phone number format: +16411222233
    useEffect(() => {
        const e164Regex = /^\+\d{11,14}$/;
        setIsValidPhone(e164Regex.test(phoneNumber));
    }, [phoneNumber]);

    const handleEnrollParticipant = () => {
        if (phoneNumber && !isValidPhone) return;
        if (!patientVisit) return;

        setIsEnrolling(true);
        callWrite({
            operation: LIDIA_ENROLL_PARTICIPANT,
            csn: patientVisit.csn,
            mrn: patientVisit.mrn,
            phone_number: phoneNumber || undefined,
            participant_type: participantType,
            status: 'ACTIVE'
        })
        .then((result: any) => {
            console.log("Participant enrolled:", result);
            // Refresh enrollment status and conversation data
            checkEnrollmentStatus();
            fetchConversationData();
            setShowEnrollModal(false);
            setPhoneNumber("");
        })
        .catch((error: any) => {
            console.error("Error enrolling participant:", error);
            alert(`Failed to enroll participant: ${error.message || 'Unknown error'}`);
        })
        .finally(() => {
            setIsEnrolling(false);
        });
    };

    const sendInitiateMessage = (activityType: string, actionableItemId?: number) => {
        if (!patientVisit) return;
        if (!conversationStatus) return;

        const payload: any = {
            operation: LIDIA_INITIATE_ACTIVITY,
            phone_number: conversationStatus.phone_number,
            csn: patientVisit.csn,
            mrn: patientVisit.mrn,
            activity_type: activityType,
        };

        // Add actionable_item_id if provided
        if (actionableItemId !== undefined) {
            payload.actionable_item_id = actionableItemId;
        }

        callWrite(payload)
        .then((status: any) => {
            setShowToast(true);
        });
    };

    const handleOpenActionableModal = () => {
        setShowActionableModal(true);
        fetchActionableItems();
    };

    const handleSelectActionableItem = (item: ActionableItem) => {
        setSelectedActionableItem(item);
    };

    const handleSimulateActionableItem = () => {
        if (selectedActionableItem && selectedActionableItem.id) {
            sendInitiateMessage("ACTIONABLE_ITEM", selectedActionableItem.id);
            setShowActionableModal(false);
            setSelectedActionableItem(null);
        }
    };

    const handleCloseConversation = () => {
        if (!conversationStatus || !conversationStatus.phone_number) return;

        if (window.confirm(`Are you sure you want to close the conversation for ${conversationStatus.phone_number}?`)) {
            callWrite({
                operation: LIDIA_CLOSE_CONVERSATION,
                phone_number: conversationStatus.phone_number,
            })
            .then(() => {
                // Refresh conversation data to update UI
                fetchConversationData();
            })
            .catch((error) => {
                console.error("Error closing conversation:", error);
            });
        }
    };

    const handleDeleteConversation = () => {
        if (!conversationStatus || !conversationStatus.phone_number || !patientVisit) return;

        if (window.confirm(`Are you sure you want to DELETE the conversation for ${conversationStatus.phone_number}? This will permanently remove the conversation from DynamoDB and delete the lidia.json file from S3. This action cannot be undone.`)) {
            callWrite({
                operation: LIDIA_DELETE_CONVERSATION,
                phone_number: conversationStatus.phone_number,
                csn: patientVisit.csn,
                mrn: patientVisit.mrn,
            })
            .then(() => {
                // Clear the conversation state immediately
                setConversation(undefined);
                setConversationStatus(undefined);
                // Refresh conversation data to update UI
                fetchConversationData();
            })
            .catch((error) => {
                console.error("Error deleting conversation:", error);
            });
        }
    };

    const isAdmin = getDerivedRole(props.user) === UserRole.ADMIN;

    if (!isAdmin) {
        return <>Must be Admin to view</>
    }

    const isWaitingForResponse = (conversation: SMSMessage[] | undefined): boolean => {
        if (!conversation || conversation.length === 0) return false;
        const lastMessage = conversation[conversation.length - 1];
        return lastMessage.expect_patient_reply === "yes";
    };

    const waitingForResponse = isWaitingForResponse(conversation);

    const getPriorityBadge = (priority?: Priority) => {
        if (!priority) return null;
        const colorMap = {
            'HIGH': 'danger',
            'MEDIUM': 'warning',
            'LOW': 'info'
        };
        return <Badge bg={colorMap[priority] || 'secondary'}>{priority}</Badge>;
    };

    const getStatusBadge = (status?: Status) => {
        if (!status) return null;
        const colorMap = {
            'OPEN': 'primary',
            'IN_PROGRESS': 'warning',
            'COMPLETED': 'success',
            'ARCHIVED': 'secondary'
        };
        return <Badge bg={colorMap[status] || 'secondary'}>{status}</Badge>;
    };

    return (
        <Container className="notes-dashboard lidia-dashboard">
            {
                props.showHeader && (
                    <Row className="mb-3">
                        <Col md={2}>
                            <span className="gradient-text" style={{fontSize: '1.25rem'}}>LIDIA</span>
                        </Col>
                        <Col className="text-right" md={10}>
                            {isLoadingParticipant ? (
                                <small>
                                    <span className="text-muted">
                                        <FontAwesomeIcon icon={faGear} spin /> Checking enrollment...
                                    </span>
                                </small>
                            ) : !participantDetails || !participantDetails.study_id ? (
                                <div>
                                    <span className="text-muted me-2">
                                        <Badge bg="warning">Not Enrolled</Badge>
                                    </span>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => setShowEnrollModal(true)}
                                    >
                                        <FontAwesomeIcon icon={faUserPlus} /> Enroll in Study
                                    </Button>
                                </div>
                            ) : isSavingOptIn ? (
                                    <small>
                                        <span className="text-muted">
                                            <FontAwesomeIcon icon={faGear} spin /> Syncing Messages...
                                        </span>
                                    </small>
                                ) : conversationStatus && conversationStatus.phone_number ? (
                                    <div>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            className="me-3"
                                            onClick={(e: any) => {fetchConversationData()}}
                                            disabled={isLoadingData}
                                        >
                                            <FontAwesomeIcon icon={faRefresh} spin={isLoadingData} />
                                        </Button>
                                        <Button
                                            className="me-3"
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => sendInitiateMessage("WR")}
                                            disabled={waitingForResponse}
                                            title={waitingForResponse ? "Cannot send new message while waiting for patient response" : "Send waiting room message"}
                                        >
                                            <FontAwesomeIcon icon={faRightToBracket} /> Simulate WR
                                        </Button>
                                        <Button
                                            className="me-3"
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => sendInitiateMessage("ROOM")}
                                            disabled={waitingForResponse}
                                            title={waitingForResponse ? "Cannot send new message while waiting for patient response" : "Send room message"}
                                        >
                                            <FontAwesomeIcon icon={faRightToBracket} /> Simulate ROOM
                                        </Button>
                                        <Button
                                            className="me-3"
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => sendInitiateMessage("PDM")}
                                            disabled={waitingForResponse || !patientVisit?.departure_time}
                                            title={waitingForResponse ? "Cannot send new message while waiting for patient response" : "Send post-discharge message"}
                                        >
                                            <FontAwesomeIcon icon={faRightToBracket} /> Simulate PDM
                                        </Button>
                                        <Button
                                            className="me-3"
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={handleOpenActionableModal}
                                            disabled={waitingForResponse}
                                            title={waitingForResponse ? "Cannot send new message while waiting for patient response" : "Send actionable item message"}
                                        >
                                            <FontAwesomeIcon icon={faRightToBracket} /> Simulate Actionable Items
                                        </Button>
                                        <Button
                                            className="me-3"
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={handleCloseConversation}
                                            title="Close this conversation"
                                        >
                                            <FontAwesomeIcon icon={faTimes} /> Close Conversation
                                        </Button>
                                        <Button
                                            className="me-3"
                                            variant="danger"
                                            size="sm"
                                            onClick={handleDeleteConversation}
                                            title="Permanently delete this conversation from DynamoDB and S3"
                                        >
                                            <FontAwesomeIcon icon={faTrash} /> Delete Conversation
                                        </Button>
                                        <span className="text-muted">
                                            <Badge bg="success" className="me-2">
                                                Study ID: {participantDetails.study_id}
                                            </Badge>
                                            <Badge bg="secondary"><strong>{conversationStatus.phone_number}</strong></Badge>
                                            {waitingForResponse && (
                                                <Badge bg="info" className="ms-2">
                                                    <FontAwesomeIcon icon={faExclamationCircle} className="me-1" />
                                                    Waiting for patient response
                                                </Badge>
                                            )}
                                        </span>
                                    </div>
                                ) : (
                                    <div>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => {
                                                setShowEnrollModal(true);
                                                if (participantDetails.phone_number) {
                                                    setPhoneNumber(participantDetails.phone_number);
                                                }
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faRightToBracket} /> Start Conversation
                                        </Button>
                                    </div>
                                )
                            }
                        </Col>
                    </Row>
                )
            }

            {/* SMS Conversation Timeline */}
            <Row>
                <Col md={12}>
                    {
                        conversation ? (
                            <ConversationTimeline messages={conversation} />
                        ) : !participantDetails || !participantDetails.study_id ? (
                            <Row className="mt-3 text-center">
                                <Col>
                                    <h6><FontAwesomeIcon icon={faWandMagicSparkles} /> Patient Not Enrolled</h6>
                                    <p className="text-muted">
                                        Enroll this patient in the LIDIA study to start conversations
                                    </p>
                                </Col>
                            </Row>
                        ) : (
                            <Row className="mt-3 text-center">
                                <Col>
                                    <h6><FontAwesomeIcon icon={faWandMagicSparkles} /> No Conversations Yet</h6>
                                    <p className="text-muted">
                                        Enrolled as Study ID: {participantDetails.study_id}. Start a conversation to begin messaging.
                                    </p>
                                </Col>
                            </Row>
                        )
                    }
                </Col>
            </Row>

            {/* Enrollment/Conversation Modal */}
            <Modal show={showEnrollModal} onHide={() => setShowEnrollModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {participantDetails && participantDetails.study_id ? 'Start Conversation' : 'Enroll in LIDIA Study'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {(!participantDetails || !participantDetails.study_id) && (
                        <div className="mb-3">
                            <p className="text-muted">
                                This will enroll the patient in the LIDIA study with:
                            </p>
                            <ul className="text-muted">
                                <li><strong>MRN:</strong> {patientVisit?.mrn}</li>
                                <li><strong>CSN:</strong> {patientVisit?.csn}</li>
                            </ul>
                        </div>
                    )}
                    <Form onSubmit={(e) => {
                        e.preventDefault();
                        if (participantDetails && participantDetails.study_id) {
                            // Start conversation
                            if (isValidPhone) {
                                setIsSavingOptIn(true);
                                callWrite({
                                    operation: LIDIA_CREATE_CONVERSATION,
                                    phone_number: phoneNumber,
                                    csn: patientVisit!.csn,
                                    mrn: patientVisit!.mrn,
                                })
                                .then(() => {
                                    fetchConversationData();
                                    setShowEnrollModal(false);
                                    setPhoneNumber("");
                                })
                                .finally(() => {
                                    setIsSavingOptIn(false);
                                });
                            }
                        } else {
                            // Enroll participant
                            handleEnrollParticipant();
                        }
                    }}>
                        <Form.Group controlId="formPhoneNumber" className="mb-3">
                            <Form.Label>
                                Phone Number {(!participantDetails || !participantDetails.study_id) && '(Optional)'}
                            </Form.Label>
                            <Form.Control
                                type="tel"
                                placeholder="+16411222233"
                                value={phoneNumber}
                                onChange={(e) => setPhoneNumber(e.target.value)}
                                isInvalid={phoneNumber !== "" && !isValidPhone}
                            />
                            <Form.Control.Feedback type="invalid">
                                Phone number must be in the format +16411222233
                            </Form.Control.Feedback>
                            <Form.Text className="text-muted">
                                E.164 format required (e.g., +16411222233)
                            </Form.Text>
                        </Form.Group>

                        {(!participantDetails || !participantDetails.study_id) && (
                            <Form.Group controlId="formParticipantType">
                                <Form.Label>Participant Type</Form.Label>
                                <Form.Select
                                    value={participantType}
                                    onChange={(e) => setParticipantType(e.target.value)}
                                >
                                    <option value="SIMULATED">Simulated/Test Participant</option>
                                    <option value="REAL">Real Participant</option>
                                </Form.Select>
                                <Form.Text className="text-muted">
                                    Select whether this is a real participant enrollment or a test/simulated participant for testing purposes
                                </Form.Text>
                            </Form.Group>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEnrollModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => {
                            if (participantDetails && participantDetails.study_id) {
                                // Start conversation
                                if (isValidPhone) {
                                    setIsSavingOptIn(true);
                                    callWrite({
                                        operation: LIDIA_CREATE_CONVERSATION,
                                        phone_number: phoneNumber,
                                        csn: patientVisit!.csn,
                                        mrn: patientVisit!.mrn,
                                    })
                                    .then(() => {
                                        fetchConversationData();
                                        setShowEnrollModal(false);
                                        setPhoneNumber("");
                                    })
                                    .finally(() => {
                                        setIsSavingOptIn(false);
                                    });
                                }
                            } else {
                                handleEnrollParticipant();
                            }
                        }}
                        disabled={
                            isEnrolling ||
                            isSavingOptIn ||
                            (participantDetails && participantDetails.study_id && !isValidPhone) ||
                            (phoneNumber !== "" && !isValidPhone)
                        }
                    >
                        {isEnrolling || isSavingOptIn ? (
                            <><FontAwesomeIcon icon={faGear} spin /> Processing...</>
                        ) : (participantDetails && participantDetails.study_id) ? (
                            'Start Conversation'
                        ) : (
                            'Enroll Participant'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Actionable Items Modal */}
            <Modal 
                show={showActionableModal} 
                onHide={() => {
                    setShowActionableModal(false);
                    setSelectedActionableItem(null);
                }}
                size="lg"
                className="lidia-dashboard"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Select Actionable Item</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isLoadingActionableItems ? (
                        <div className="text-center py-4">
                            <FontAwesomeIcon icon={faGear} spin size="2x" />
                            <p className="mt-2 text-muted">Loading actionable items...</p>
                        </div>
                    ) : actionableItems.length === 0 ? (
                        <div className="text-center py-4">
                            <p className="text-muted">No actionable items found for this visit.</p>
                        </div>
                    ) : (
                        <ListGroup>
                            {actionableItems.map((item) => (
                                <ListGroup.Item
                                    key={item.id}
                                    action
                                    active={selectedActionableItem?.id === item.id}
                                    onClick={() => handleSelectActionableItem(item)}
                                    className="mb-2"
                                >
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div className="flex-grow-1">
                                            <div className="mb-2">
                                                <Badge bg={item.item_type === 'QUESTION' ? 'primary' : 'info'} className="me-2">
                                                    {item.item_type}
                                                </Badge>
                                                {item.category && (
                                                    <Badge bg="secondary" className="me-2">{item.category}</Badge>
                                                )}
                                                {getPriorityBadge(item.priority)}
                                                {' '}
                                                {getStatusBadge(item.status)}
                                            </div>
                                            <div className="fw-bold mb-1">{item.body}</div>
                                            {item.supporting_context && (
                                                <small className="text-muted d-block mt-1">
                                                    {item.supporting_context}
                                                </small>
                                            )}
                                            {item.executor_role && (
                                                <small className="text-muted d-block mt-1">
                                                    Assigned to: {item.executor_role}
                                                </small>
                                            )}
                                        </div>
                                    </div>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="secondary" 
                        onClick={() => {
                            setShowActionableModal(false);
                            setSelectedActionableItem(null);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleSimulateActionableItem}
                        disabled={!selectedActionableItem}
                    >
                        Simulate Selected Item
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Toast Notification */}
            <ToastContainer position="top-end" className="p-3">
                <Toast 
                    show={showToast} 
                    onClose={() => setShowToast(false)} 
                    delay={15000} 
                    autohide
                    bg="success"
                >
                    <Toast.Header>
                        <strong className="me-auto">Message Sent</strong>
                    </Toast.Header>
                    <Toast.Body className="text-white">
                        You should receive a text message within the next minute.
                    </Toast.Body>
                </Toast>
            </ToastContainer>
        </Container>
    );
};
